"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AltiumSpecialStringResolver = exports.ExpressionTermKind = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
var ExpressionTermKind;
(function (ExpressionTermKind) {
    ExpressionTermKind["Literal"] = "literal";
    ExpressionTermKind["Identifier"] = "identifier";
})(ExpressionTermKind || (exports.ExpressionTermKind = ExpressionTermKind = {}));
class AltiumSpecialStringResolver {
    static { this.SPECIAL_STRING_PATTERN = /=([A-Za-z][A-Za-z0-9_]*)/g; }
    constructor(context, parentOrParamResolver, paramResolver) {
        this.context = context;
        this.parent = parentOrParamResolver instanceof AltiumSpecialStringResolver ? parentOrParamResolver : undefined;
        this.paramResolver =
            paramResolver ??
                (parentOrParamResolver instanceof AltiumSpecialStringResolver ? undefined : parentOrParamResolver);
    }
    createChildResolver(contextOverrides) {
        return new AltiumSpecialStringResolver(contextOverrides, this);
    }
    containsSpecialString(text) {
        return (AltiumSpecialStringResolver.extractWholeStringFormName(text) !== undefined ||
            AltiumSpecialStringResolver.containsEmbeddedToken(text));
    }
    resolve(text) {
        return this.resolveText(text, new Set());
    }
    resolveText(text, activeTexts) {
        if (activeTexts.has(text)) {
            return text;
        }
        activeTexts.add(text);
        try {
            const expressionTerms = AltiumSpecialStringResolver.parseConcatenationExpression(text);
            if (expressionTerms !== undefined) {
                return this.evaluateConcatenation(expressionTerms, activeTexts) ?? text;
            }
            const wholeStringName = AltiumSpecialStringResolver.extractWholeStringFormName(text);
            if (wholeStringName !== undefined) {
                const value = this.resolveVariable(wholeStringName);
                if (value !== undefined) {
                    return this.resolveText(value, activeTexts);
                }
                if (!AltiumSpecialStringResolver.containsEmbeddedToken(text.slice(1))) {
                    return text;
                }
            }
            AltiumSpecialStringResolver.SPECIAL_STRING_PATTERN.lastIndex = 0;
            return text.replace(AltiumSpecialStringResolver.SPECIAL_STRING_PATTERN, (match, variableName) => {
                const value = this.resolveVariable(variableName);
                return value !== undefined ? this.resolveText(value, activeTexts) : match;
            });
        }
        finally {
            activeTexts.delete(text);
        }
    }
    evaluateConcatenation(terms, activeTexts) {
        let result = '';
        for (const term of terms) {
            if (term.kind === ExpressionTermKind.Literal) {
                result += term.value;
                continue;
            }
            const value = this.resolveVariable(term.value);
            if (value === undefined) {
                return undefined;
            }
            result += this.resolveText(value, activeTexts);
        }
        return result;
    }
    static parseConcatenationExpression(text) {
        if (!text.startsWith('=') || /^=\s/.test(text)) {
            return undefined;
        }
        const tokenPattern = /\s*(?:'([^']*)'|"([^"]*)"|([A-Za-z_][A-Za-z0-9_]*))\s*/y;
        const terms = [];
        let position = 1;
        for (;;) {
            tokenPattern.lastIndex = position;
            const match = tokenPattern.exec(text);
            if (match === null) {
                return undefined;
            }
            const [, singleQuoted, doubleQuoted, identifier] = match;
            terms.push(identifier !== undefined ?
                { kind: ExpressionTermKind.Identifier, value: identifier }
                : { kind: ExpressionTermKind.Literal, value: singleQuoted ?? doubleQuoted ?? '' });
            position = tokenPattern.lastIndex;
            if (position === text.length) {
                break;
            }
            if (text[position] !== '+') {
                return undefined;
            }
            position += 1;
        }
        return terms.length > 1 ? terms : undefined;
    }
    static containsEmbeddedToken(text) {
        AltiumSpecialStringResolver.SPECIAL_STRING_PATTERN.lastIndex = 0;
        return AltiumSpecialStringResolver.SPECIAL_STRING_PATTERN.test(text);
    }
    static extractWholeStringFormName(text) {
        if (!text.startsWith('=')) {
            return undefined;
        }
        const remainder = text.slice(1);
        if (remainder === '' || remainder !== remainder.trimStart()) {
            return undefined;
        }
        const trimmedRemainder = remainder.trimEnd();
        const quoted = /^"(.*)"$/.exec(trimmedRemainder) ?? /^'(.*)'$/.exec(trimmedRemainder);
        const variableName = quoted ? quoted[1] : trimmedRemainder;
        if (variableName === '') {
            return undefined;
        }
        return variableName;
    }
    resolveVariable(variableName) {
        const normalizedName = variableName.toLowerCase();
        let value = this.valueToString(this.findInContext(normalizedName, this.context));
        if (!this.isEmpty(value)) {
            return value;
        }
        value = this.paramResolver?.(variableName);
        if (!this.isEmpty(value)) {
            return value;
        }
        return this.parent?.resolveVariable(variableName);
    }
    isEmpty(value) {
        return value === undefined || value === null || value === '' || value === '*';
    }
    findInContext(normalizedName, context) {
        const contextKey = Object.keys(context).find((k) => k.toLowerCase() === normalizedName);
        if (contextKey === undefined) {
            return undefined;
        }
        const thunkValue = context[contextKey];
        return sigma_engine_shared_1.Utils.resolveValueFromThunk(thunkValue);
    }
    valueToString(value) {
        if (value === undefined) {
            return undefined;
        }
        return String(value);
    }
}
exports.AltiumSpecialStringResolver = AltiumSpecialStringResolver;
//# sourceMappingURL=AltiumSpecialStringResolver.js.map